import React, { useMemo } from 'react';
import { useMatches } from 'react-router-dom';
import { SlideLayout } from '../slide-layout/SlideLayout';
import { ColumnData } from '../slide-layout/interfaces';

export type PanelMeta = { key: string; title: string; rawTitle?: string };
export type RouteHandle = { panel?: PanelMeta };

export type PanelShellProps = {
  leftColumn: ColumnData;
  /**
   * When set, this prop is forwarded to SlideLayout's `rightColumn` and takes
   * precedence over any column registered via `useSlidePanel` (e.g. the assets
   * selection panel while rows are selected).
   */
  rightColumnOverride?: ColumnData;
  /**
   * Additional fields merged into the outlet context alongside `SlidePanelRegistrar`.
   * Passed through to SlideLayout's `outletContext` prop.
   */
  outletContextExtras?: object;
  /**
   * Override the number of visible panel columns.
   * Defaults to 1 on compact (phone/small tablet) and 2 on wide screens.
   */
  visibleCount?: number;
};

/**
 * Generic section root that wraps `SlideLayout` and drives its `transitionKey`
 * from the active route's `handle.panel` declarations.
 *
 * P1: child route components still register their `ColumnData` via `useSlidePanel`
 * through the `<Outlet />` that `SlideLayout` renders when `enableChildRoutes` is
 * set. `PanelShell` uses `stack` today only for:
 *   - `transitionKey` — drives the slide-in animation per navigation event
 *   - future dev warnings ("route declared handle.panel but didn't register column")
 *
 * P2 (follow-up PR): shell renders `handle.panel.Component` at stable React keys
 * and `useSlidePanel` is retired.
 */
export const PanelShell: React.FC<PanelShellProps> = ({
  leftColumn,
  rightColumnOverride,
  outletContextExtras,
}) => {
  const matches = useMatches();

  // Collect the panel metadata declared on every matched route in the active tree.
  const stack = useMemo(
    () =>
      matches
        .map((m) => (m.handle as RouteHandle | undefined)?.panel)
        .filter((p): p is PanelMeta => !!p),
    [matches]
  );

  // Changing transitionKey while in DUAL mode triggers a slide-in animation for
  // the left column, giving per-navigation visual feedback.
  const transitionKey = stack.map((p) => p.key).join('>');

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumnOverride}
      transitionKey={transitionKey}
      enableChildRoutes
      outletContext={outletContextExtras}
    />
  );
};
